<div class="w-full">
  <div *ngIf="loading" class="py-6 text-sm text-gray-500">
    {{ 'TERMS_PARAMS.LOADING' | translate }}
  </div>

  <div *ngIf="!loading" class="space-y-6">
    <!-- HEADER / ADD BUTTON -->
    <section class="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 class="text-base font-semibold text-indigo-900">
            {{ 'TERMS_PARAMS.REPAYMENT_TERMS' | translate }}
          </h3>
          <p class="mt-1 text-xs text-gray-500">
            {{ 'TERMS_PARAMS.REPAYMENT_TERMS_HINT' | translate }}
          </p>
        </div>

        <button
          *ngIf="editable"
          type="button"
          class="btn-primary"
          [disabled]="saving || !availableTermOptions.length"
          (click)="openAddTermPopup()"
        >
          {{ 'TERMS_PARAMS.ADD_REPAYMENT_TERM_BUTTON' | translate }}
        </button>
      </div>

      <div *ngIf="loadingSavedData" class="mt-3 text-sm text-gray-500">
        {{ 'TERMS_PARAMS.LOADING_SAVED' | translate }}
      </div>

      <p
        *ngIf="!loadingSavedData && repaymentTermsArray.length === 0"
        class="mt-3 text-xs text-red-500"
      >
        {{ 'TERMS_PARAMS.NO_TERMS_ADDED' | translate }}
      </p>

      <p
        *ngIf="editable && !availableTermOptions.length && termOptions.length"
        class="mt-3 text-xs text-gray-500"
      >
        {{ 'TERMS_PARAMS.ALL_TERMS_ADDED' | translate }}
      </p>
    </section>

    <!-- SHARED FIELD TEMPLATE (used for both the added terms and the add-term popup) -->
    <ng-template #termFieldsTemplate let-meta="meta">
      <!-- Disbursement Details -->
      <div class="mb-6">
        <h4 class="mb-4 text-sm font-semibold text-gray-700">
          {{ 'TERMS_PARAMS.DISBURSEMENT_DETAILS' | translate }}
        </h4>

        <div
          class="grid grid-cols-1 gap-4 md:grid-cols-4"
          [formGroup]="meta.group"
        >
          <div>
            <label class="form-label mb-2 block whitespace-nowrap">
              {{ 'TERMS_PARAMS.DISBURSEMENT_AMOUNT' | translate }}
              <span class="text-red-500">*</span>
            </label>

            <input
              type="text"
              appAmountInput
              formControlName="disbursementAmount"
              class="form-input w-full"
              [class.form-error]="isTermControlInvalid(meta, 'disbursementAmount')"
            />

            <p
              *ngIf="isTermControlInvalid(meta, 'disbursementAmount')"
              class="form-error-text"
            >
              {{ getTermControlError(meta, 'disbursementAmount', 'TERMS_PARAMS.DISBURSEMENT_AMOUNT') }}
            </p>
          </div>

          <div>
            <label class="form-label mb-2 block whitespace-nowrap">
              {{ 'TERMS_PARAMS.DISBURSEMENT_START_DATE' | translate }}
              <span class="text-red-500">*</span>
            </label>

            <input
              type="date"
              formControlName="disbursementStartDate"
              class="form-input w-full"
              (keydown)="blockManualDateEntry($event)"
              (paste)="$event.preventDefault()"
              [class.form-error]="isTermControlInvalid(meta, 'disbursementStartDate')"
            />

            <p
              *ngIf="isTermControlInvalid(meta, 'disbursementStartDate')"
              class="form-error-text"
            >
              {{ getTermControlError(meta, 'disbursementStartDate', 'TERMS_PARAMS.DISBURSEMENT_START_DATE') }}
            </p>
          </div>

          <div>
            <label class="form-label mb-2 block whitespace-nowrap">
              {{ 'TERMS_PARAMS.DISBURSEMENT_END_DATE' | translate }}
              <span class="text-red-500">*</span>
            </label>

            <input
              type="date"
              formControlName="disbursementEndDate"
              class="form-input w-full"
              (keydown)="blockManualDateEntry($event)"
              (paste)="$event.preventDefault()"
              [class.form-error]="isTermControlInvalid(meta, 'disbursementEndDate')"
            />

            <p
              *ngIf="isTermControlInvalid(meta, 'disbursementEndDate')"
              class="form-error-text"
            >
              {{ getTermControlError(meta, 'disbursementEndDate', 'TERMS_PARAMS.DISBURSEMENT_END_DATE') }}
            </p>
          </div>

          <div>
            <label class="form-label mb-2 block whitespace-nowrap">
              {{ 'TERMS_PARAMS.KIBOR_SPREAD_RATE' | translate }}
              <span class="text-red-500">*</span>
            </label>

            <div class="relative">
              <input
                type="number"
                min="0"
                step="0.01"
                inputmode="decimal"
                formControlName="kiborSpreadRate"
                class="form-input w-full pe-10"
                [class.form-error]="isTermControlInvalid(meta, 'kiborSpreadRate')"
              />
              <span class="percent">%</span>
            </div>

            <p
              *ngIf="isTermControlInvalid(meta, 'kiborSpreadRate')"
              class="form-error-text"
            >
              {{ getTermControlError(meta, 'kiborSpreadRate', 'TERMS_PARAMS.KIBOR_SPREAD_RATE') }}
            </p>
          </div>
        </div>

        <p
          *ngIf="
            hasTermError(meta, 'disbursementDateRange') &&
            (meta.group.controls.disbursementStartDate.touched ||
              meta.group.controls.disbursementEndDate.touched)
          "
          class="form-error-text mt-3"
        >
          {{ 'TERMS_PARAMS.DISBURSEMENT_DATE_RANGE_ERROR' | translate }}
        </p>
      </div>

      <!-- KIBOR Details -->
      <div class="mb-6 border-t border-gray-100 pt-6" [formGroup]="meta.group">
        <h4 class="mb-4 text-sm font-semibold text-gray-700">
          {{ 'TERMS_PARAMS.KIBOR_DETAILS' | translate }}
        </h4>

        <div>
          <label class="form-label mb-2 block">
            {{ 'TERMS_PARAMS.KIBOR_VARIABLE' | translate }}
            <span class="text-red-500">*</span>
          </label>

          <div class="radio-toggle-group">
            <ng-container *ngFor="let option of yesNoOptions">
              <input
                type="radio"
                [id]="'kiborVariable_' + meta.termRefCdSeq + '_' + option.value"
                formControlName="kiborVariable"
                [value]="option.value"
              />
              <label [for]="'kiborVariable_' + meta.termRefCdSeq + '_' + option.value">
                {{ option.label | translate }}
              </label>
            </ng-container>
          </div>
        </div>

        <div
          *ngIf="meta.group.controls.kiborVariable.value === 1"
          class="mt-6 grid grid-cols-1 gap-6 rounded-lg border border-indigo-100 bg-indigo-50/40 p-4 md:grid-cols-2"
        >
          <div>
            <label class="form-label mb-2 block">
              {{ 'TERMS_PARAMS.KIBOR_DAY' | translate }}
              <span class="text-red-500">*</span>
            </label>

            <select
              formControlName="kiborDay"
              class="form-select w-full"
              [class.form-error]="isTermControlInvalid(meta, 'kiborDay')"
            >
              <option [ngValue]="null">{{ 'TERMS_PARAMS.SELECT_DAY' | translate }}</option>
              <option *ngFor="let day of kiborDayOptions" [ngValue]="day">
                {{ day }}
              </option>
            </select>

            <p
              *ngIf="isTermControlInvalid(meta, 'kiborDay')"
              class="form-error-text"
            >
              {{ getTermControlError(meta, 'kiborDay', 'TERMS_PARAMS.KIBOR_DAY') }}
            </p>
          </div>

          <div>
            <label class="form-label mb-2 block">
              {{ 'TERMS_PARAMS.KIBOR_RESET_PERIOD' | translate }}
              <span class="text-red-500">*</span>
            </label>

            <select
              formControlName="kiborResetPeriod"
              class="form-select w-full"
              [class.form-error]="isTermControlInvalid(meta, 'kiborResetPeriod')"
            >
              <option [ngValue]="null">{{ 'TERMS_PARAMS.SELECT_RESET_PERIOD' | translate }}</option>
              <option
                *ngFor="let option of kiborResetPeriodOptions"
                [ngValue]="option.value"
              >
                {{ option.label | translate }}
              </option>
            </select>

            <p
              *ngIf="isTermControlInvalid(meta, 'kiborResetPeriod')"
              class="form-error-text"
            >
              {{ getTermControlError(meta, 'kiborResetPeriod', 'TERMS_PARAMS.KIBOR_RESET_PERIOD') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Guarantor Details -->
      <div class="mb-6 border-t border-gray-100 pt-6" [formGroup]="meta.group">
        <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h4 class="text-sm font-semibold text-gray-700">
            {{ 'TERMS_PARAMS.GUARANTOR_DETAILS' | translate }}
          </h4>

          <div
            *ngIf="guarantorsArrayOf(meta).length"
            class="rounded-full px-3 py-1 text-sm font-semibold"
            [class.bg-emerald-50]="!isGuarantorTotalExceeded(meta)"
            [class.text-emerald-700]="!isGuarantorTotalExceeded(meta)"
            [class.bg-red-50]="isGuarantorTotalExceeded(meta)"
            [class.text-red-700]="isGuarantorTotalExceeded(meta)"
          >
            {{ 'TERMS_PARAMS.TOTAL_PERCENT' | translate: { value: (guarantorTotalForTerm(meta) | number: '1.0-2') } }}
          </div>
        </div>

        <div class="mb-4">
          <label class="form-label mb-2 block">
            {{ 'TERMS_PARAMS.GUARANTOR_FLAG' | translate }}
            <span class="text-red-500">*</span>
          </label>

          <div class="radio-toggle-group">
            <ng-container *ngFor="let option of yesNoOptions">
              <input
                type="radio"
                [id]="'guarantorFlag_' + meta.termRefCdSeq + '_' + option.value"
                formControlName="guarantorFlag"
                [value]="option.value"
              />
              <label [for]="'guarantorFlag_' + meta.termRefCdSeq + '_' + option.value">
                {{ option.label | translate }}
              </label>
            </ng-container>
          </div>
        </div>

        <ng-container *ngIf="meta.group.controls.guarantorFlag.value === 1">
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
            <div>
              <label class="form-label mb-2 block">
                {{ 'TERMS_PARAMS.SELECT_GUARANTORS' | translate }}
              </label>

              <select
                multiple
                [formControl]="meta.guarantorSelectionControl"
                class="form-select min-h-24 w-full"
              >
                <option
                  *ngFor="let option of availableGuarantorsForTerm(meta)"
                  [ngValue]="option.value"
                >
                  {{ option.label | translate }}
                </option>
              </select>
            </div>

            <button
              *ngIf="editable"
              type="button"
              class="btn-primary self-end"
              [disabled]="saving || meta.guarantorSelectionControl.value.length === 0"
              (click)="addGuarantorsToTerm(meta)"
            >
              {{ 'TERMS_PARAMS.ADD_SELECTED' | translate }}
            </button>
          </div>

          <div
            *ngIf="guarantorsArrayOf(meta).length"
            class="mt-4 space-y-3"
          >
            <div formArrayName="guarantors">
              <div
                *ngFor="let guarantorGroup of guarantorsArrayOf(meta).controls; let j = index"
                [formGroupName]="j"
                class="flex flex-wrap items-center gap-4 rounded-lg border border-gray-200 bg-gray-50 p-3"
              >
                <div class="min-w-40 flex-1 text-sm font-medium text-gray-700">
                  {{ guarantorLabel(guarantorGroup.controls.guarantorRefCdSeq.value) | translate }}
                </div>

                <div class="w-40">
                  <div class="relative">
                    <input
                      type="number"
                      min="0.01"
                      max="100"
                      step="0.01"
                      inputmode="decimal"
                      formControlName="percentage"
                      class="form-input w-full pe-10"
                      [class.form-error]="isGuarantorPercentageInvalid(meta, j)"
                    />
                    <span class="percent">%</span>
                  </div>
                </div>

                <button
                  *ngIf="editable"
                  type="button"
                  class="rounded-lg border border-red-200 px-3 py-1.5 text-sm font-semibold text-red-600 hover:bg-red-50"
                  [disabled]="saving"
                  (click)="removeGuarantorFromTerm(meta, j)"
                >
                  {{ 'COMMON.REMOVE' | translate }}
                </button>

                <p
                  *ngIf="isGuarantorPercentageInvalid(meta, j)"
                  class="form-error-text w-full"
                >
                  {{ getGuarantorPercentageError(meta, j) }}
                </p>
              </div>
            </div>
          </div>

          <p
            *ngIf="isGuarantorTotalExceeded(meta)"
            class="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
          >
            {{ 'TERMS_PARAMS.GUARANTOR_TOTAL_EXCEEDED' | translate: { total: (guarantorTotalForTerm(meta) | number: '1.0-2') } }}
          </p>

          <p
            *ngIf="guarantorsArrayOf(meta).length && !isGuarantorTotalExceeded(meta)"
            class="mt-3 text-xs text-gray-500"
          >
            {{ 'TERMS_PARAMS.REMAINING_ALLOCATION' | translate: { value: (remainingGuarantorPercentage(meta) | number: '1.0-2') } }}
          </p>
        </ng-container>
      </div>

      <!-- Renewable Amount -->
      <div class="border-t border-gray-100 pt-6" [formGroup]="meta.group">
        <h4 class="mb-4 text-sm font-semibold text-gray-700">
          {{ 'TERMS_PARAMS.RENEWABLE_AMOUNT' | translate }}
        </h4>

        <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <label class="form-label mb-2 block">
              {{ 'TERMS_PARAMS.RENEWABLE' | translate }}
              <span class="text-red-500">*</span>
            </label>

            <div class="radio-toggle-group">
              <ng-container *ngFor="let option of yesNoOptions">
                <input
                  type="radio"
                  [id]="'isRenewable_' + meta.termRefCdSeq + '_' + option.value"
                  formControlName="isRenewable"
                  [value]="option.value"
                />
                <label [for]="'isRenewable_' + meta.termRefCdSeq + '_' + option.value">
                  {{ option.label | translate }}
                </label>
              </ng-container>
            </div>
          </div>

          <div *ngIf="meta.group.controls.isRenewable.value === 1">
            <label class="form-label mb-2 block">
              {{ 'TERMS_PARAMS.RENEWABLE_AMOUNT' | translate }}
              <span class="text-red-500">*</span>
            </label>

            <input
              type="text"
              appAmountInput
              formControlName="renewableAmount"
              class="form-input w-full"
              [class.form-error]="isTermControlInvalid(meta, 'renewableAmount')"
            />

            <p
              *ngIf="isTermControlInvalid(meta, 'renewableAmount')"
              class="form-error-text"
            >
              {{ getTermControlError(meta, 'renewableAmount', 'TERMS_PARAMS.RENEWABLE_AMOUNT') }}
            </p>

            <p
              *ngIf="
                hasTermError(meta, 'renewableAmountExceedsDisbursement') &&
                (meta.group.controls.renewableAmount.touched ||
                  meta.group.controls.disbursementAmount.touched)
              "
              class="form-error-text"
            >
              {{ 'TERMS_PARAMS.RENEWABLE_EXCEEDS_ERROR' | translate }}
            </p>
          </div>
        </div>
      </div>

      <!-- Prime Amount -->
      <div class="mt-6 border-t border-gray-100 pt-6" [formGroup]="meta.group">
        <h4 class="mb-4 text-sm font-semibold text-gray-700">
          {{ 'TERMS_PARAMS.PRIME_AMOUNT' | translate }}
        </h4>

        <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <label class="form-label mb-2 block">
              {{ 'TERMS_PARAMS.PRIME' | translate }}
              <span class="text-red-500">*</span>
            </label>

            <div class="radio-toggle-group">
              @for (option of yesNoOptions; track option.value) {
                <input
                  type="radio"
                  [id]="'isPrime_' + meta.termRefCdSeq + '_' + option.value"
                  formControlName="isPrime"
                  [value]="option.value"
                />
                <label [for]="'isPrime_' + meta.termRefCdSeq + '_' + option.value">
                  {{ option.label | translate }}
                </label>
              }
            </div>
          </div>

          @if (meta.group.controls.isPrime.value === 1) {
            <div>
              <label class="form-label mb-2 block">
                {{ 'TERMS_PARAMS.PRIME_AMOUNT' | translate }}
                <span class="text-red-500">*</span>
              </label>

              <input
                type="text"
                appAmountInput
                formControlName="primeAmount"
                class="form-input w-full"
                [class.form-error]="isTermControlInvalid(meta, 'primeAmount')"
              />

              @if (isTermControlInvalid(meta, 'primeAmount')) {
                <p class="form-error-text">
                  {{ getTermControlError(meta, 'primeAmount', 'TERMS_PARAMS.PRIME_AMOUNT') }}
                </p>
              }

              @if (
                hasTermError(meta, 'primeAmountExceedsDisbursement') &&
                (meta.group.controls.primeAmount.touched ||
                  meta.group.controls.disbursementAmount.touched)
              ) {
                <p class="form-error-text">
                  {{ 'TERMS_PARAMS.PRIME_EXCEEDS_ERROR' | translate }}
                </p>
              }
            </div>
          }
        </div>
      </div>
    </ng-template>

    <!-- ADDED REPAYMENT TERM SECTIONS -->
    <section
      *ngFor="let meta of termsMeta; trackBy: trackByTerm"
      class="rounded-xl border border-gray-200 bg-white p-4 sm:p-6"
    >
      <div class="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h3 class="text-base font-semibold text-indigo-900">
          {{ termLabel(meta.termRefCdSeq) | translate }}
        </h3>

        <button
          *ngIf="editable"
          type="button"
          class="rounded-lg border border-red-200 px-3 py-1.5 text-sm font-semibold text-red-600 hover:bg-red-50"
          [disabled]="saving"
          (click)="removeTermSection(meta)"
        >
          {{ 'TERMS_PARAMS.REMOVE_TERM' | translate }}
        </button>
      </div>

      <ng-container
        *ngTemplateOutlet="termFieldsTemplate; context: { meta: meta }"
      ></ng-container>
    </section>

    <!-- REPAYMENT / ARREARS / UPFRONT CHARGES (shared across all repayment terms) -->
    <section
      *ngIf="termsMeta.length"
      class="rounded-xl border border-gray-200 bg-white p-4 sm:p-6"
      [formGroup]="globalDetailsForm"
    >
      <h3 class="mb-5 text-base font-semibold text-indigo-900">
        {{ 'TERMS_PARAMS.REPAYMENT_DETAILS_TITLE' | translate }}
      </h3>

      <div class="grid grid-cols-1 gap-6 md:grid-cols-3">
        <!-- Repayment Start Date -->
        <div>
          <label class="form-label mb-2 block">
            {{ 'TERMS_PARAMS.REPAYMENT_START_DATE' | translate }}
            <span class="text-red-500">*</span>
          </label>

          <input
            type="date"
            formControlName="repaymentStartDate"
            class="form-input w-full"
            (keydown)="blockManualDateEntry($event)"
            (paste)="$event.preventDefault()"
            [class.form-error]="
              globalDetailsForm.controls.repaymentStartDate.invalid &&
              (globalDetailsForm.controls.repaymentStartDate.dirty ||
                globalDetailsForm.controls.repaymentStartDate.touched)
            "
          />

          <p
            *ngIf="
              globalDetailsForm.controls.repaymentStartDate.invalid &&
              (globalDetailsForm.controls.repaymentStartDate.dirty ||
                globalDetailsForm.controls.repaymentStartDate.touched)
            "
            class="form-error-text"
          >
            {{ 'TERMS_PARAMS.REPAYMENT_START_REQUIRED' | translate }}
          </p>

          <p
            *ngIf="
              isRepaymentBeforeDisbursement &&
              globalDetailsForm.controls.repaymentStartDate.touched
            "
            class="form-error-text"
          >
            {{ 'TERMS_PARAMS.REPAYMENT_BEFORE_DISBURSEMENT_DATE' | translate: { date: earliestDisbursementStartDate } }}
          </p>
        </div>

        <!-- Repayment End Date -->
        <div>
          <label class="form-label mb-2 block">
            {{ 'TERMS_PARAMS.REPAYMENT_END_DATE' | translate }}
            <span class="text-red-500">*</span>
          </label>

          <input
            type="date"
            formControlName="repaymentEndDate"
            class="form-input w-full"
            (keydown)="blockManualDateEntry($event)"
            (paste)="$event.preventDefault()"
            [class.form-error]="
              globalDetailsForm.controls.repaymentEndDate.invalid &&
              (globalDetailsForm.controls.repaymentEndDate.dirty ||
                globalDetailsForm.controls.repaymentEndDate.touched)
            "
          />

          <p
            *ngIf="
              globalDetailsForm.controls.repaymentEndDate.invalid &&
              (globalDetailsForm.controls.repaymentEndDate.dirty ||
                globalDetailsForm.controls.repaymentEndDate.touched)
            "
            class="form-error-text"
          >
            {{ 'TERMS_PARAMS.REPAYMENT_END_REQUIRED' | translate }}
          </p>

          <p
            *ngIf="
              globalDetailsForm.hasError('repaymentDateRange') &&
              (globalDetailsForm.controls.repaymentStartDate.touched ||
                globalDetailsForm.controls.repaymentEndDate.touched)
            "
            class="form-error-text"
          >
            {{ 'TERMS_PARAMS.REPAYMENT_DATE_RANGE_ERROR' | translate }}
          </p>
        </div>

        <!-- Installment Frequency -->
        <div>
          <label class="form-label mb-2 block">
            {{ 'TERMS_PARAMS.INSTALLMENT_FREQUENCY' | translate }}
            <span class="text-red-500">*</span>
          </label>

          <select
            formControlName="installmentFrequency"
            class="form-select w-full"
            [class.form-error]="
              globalDetailsForm.controls.installmentFrequency.invalid &&
              (globalDetailsForm.controls.installmentFrequency.dirty ||
                globalDetailsForm.controls.installmentFrequency.touched)
            "
          >
            <option [ngValue]="null">{{ 'TERMS_PARAMS.SELECT_FREQUENCY' | translate }}</option>
            <option
              *ngFor="let option of frequencyOptions"
              [ngValue]="option.value"
            >
              {{ option.label | translate }}
            </option>
          </select>

          <p
            *ngIf="
              globalDetailsForm.controls.installmentFrequency.invalid &&
              (globalDetailsForm.controls.installmentFrequency.dirty ||
                globalDetailsForm.controls.installmentFrequency.touched)
            "
            class="form-error-text"
          >
            {{ 'TERMS_PARAMS.INSTALLMENT_FREQUENCY_REQUIRED' | translate }}
          </p>
        </div>

        <!-- Credit Ensurance Fee -->
        <div>
          <label class="form-label mb-2 block">
            {{ 'TERMS_PARAMS.CREDIT_ENSURANCE_FEE' | translate }}
          </label>

          <input
            type="text"
            inputmode="decimal"
            formControlName="creditEnsuranceFee"
            class="form-input w-full"
            [class.form-error]="
              globalDetailsForm.controls.creditEnsuranceFee.invalid &&
              (globalDetailsForm.controls.creditEnsuranceFee.dirty ||
                globalDetailsForm.controls.creditEnsuranceFee.touched)
            "
          />

          <p
            *ngIf="
              globalDetailsForm.controls.creditEnsuranceFee.invalid &&
              (globalDetailsForm.controls.creditEnsuranceFee.dirty ||
                globalDetailsForm.controls.creditEnsuranceFee.touched)
            "
            class="form-error-text"
          >
            {{ 'TERMS_PARAMS.CREDIT_ENSURANCE_FEE_ERROR' | translate }}
          </p>
        </div>

        <!-- Grace Period -->
        <div>
          <label class="form-label mb-2 block">
            {{ 'TERMS_PARAMS.GRACE_PERIOD_MONTHS' | translate }}
            <span class="text-red-500">*</span>
          </label>

          <input
            type="number"
            min="0"
            max="999"
            maxlength="3"
            step="1"
            formControlName="gracePeriod"
            class="form-input w-full"
            [class.form-error]="
              globalDetailsForm.controls.gracePeriod.invalid &&
              (globalDetailsForm.controls.gracePeriod.dirty ||
                globalDetailsForm.controls.gracePeriod.touched)
            "
          />

          <p
            *ngIf="
              globalDetailsForm.controls.gracePeriod.invalid &&
              (globalDetailsForm.controls.gracePeriod.dirty ||
                globalDetailsForm.controls.gracePeriod.touched)
            "
            class="form-error-text"
          >
            {{ 'TERMS_PARAMS.GRACE_PERIOD_ERROR' | translate }}
          </p>
        </div>

        <!-- Number of Installments -->
        <div>
          <label class="form-label mb-2 block">
            {{ 'TERMS_PARAMS.NUMBER_OF_INSTALLMENTS' | translate }}
            <span class="text-red-500">*</span>
          </label>

          <input
            type="number"
            min="1"
            max="999"
            maxlength="3"
            step="1"
            formControlName="numberOfInstallments"
            class="form-input w-full"
            [class.form-error]="
              globalDetailsForm.controls.numberOfInstallments.invalid &&
              (globalDetailsForm.controls.numberOfInstallments.dirty ||
                globalDetailsForm.controls.numberOfInstallments.touched)
            "
          />

          <p
            *ngIf="
              globalDetailsForm.controls.numberOfInstallments.invalid &&
              (globalDetailsForm.controls.numberOfInstallments.dirty ||
                globalDetailsForm.controls.numberOfInstallments.touched)
            "
            class="form-error-text"
          >
            {{ 'TERMS_PARAMS.NUMBER_OF_INSTALLMENTS_ERROR' | translate }}
          </p>
        </div>

        <!-- Proposed Exposure -->
        <div>
          <label class="form-label mb-2 block">
            {{ 'TERMS_PARAMS.PROPOSED_EXPOSURE' | translate }}
            <span class="text-red-500">*</span>
          </label>

          <input
            type="text"
            appAmountInput
            formControlName="proposedExposure"
            class="form-input w-full"
            [class.form-error]="
              globalDetailsForm.controls.proposedExposure.invalid &&
              (globalDetailsForm.controls.proposedExposure.dirty ||
                globalDetailsForm.controls.proposedExposure.touched)
            "
          />

          <p
            *ngIf="
              globalDetailsForm.controls.proposedExposure.errors?.['maxProposedExposure'] &&
              (globalDetailsForm.controls.proposedExposure.dirty ||
                globalDetailsForm.controls.proposedExposure.touched)
            "
            class="form-error-text"
          >
            {{ 'TERMS_PARAMS.PROPOSED_EXPOSURE_MAX_ERROR' | translate: { amount: totalDisbursementAmount } }}
          </p>

          <p
            *ngIf="
              !globalDetailsForm.controls.proposedExposure.errors?.['maxProposedExposure'] &&
              globalDetailsForm.controls.proposedExposure.invalid &&
              (globalDetailsForm.controls.proposedExposure.dirty ||
                globalDetailsForm.controls.proposedExposure.touched)
            "
            class="form-error-text"
          >
            {{ 'TERMS_PARAMS.PROPOSED_EXPOSURE_ERROR' | translate }}
          </p>
        </div>

        <!-- Arrears -->
        <div>
          <label class="form-label mb-2 block">
            {{ 'TERMS_PARAMS.IN_ARREARS' | translate }}
            <span class="text-red-500">*</span>
          </label>

          <div class="radio-toggle-group">
            <ng-container *ngFor="let option of yesNoOptions">
              <input
                type="radio"
                [id]="'globalInArrears_' + option.value"
                formControlName="inArrears"
                [value]="option.value"
              />
              <label [for]="'globalInArrears_' + option.value">
                {{ option.label | translate }}
              </label>
            </ng-container>
          </div>
        </div>

        <!-- Upfront Charges -->
        <div>
          <label class="form-label mb-2 block">
            {{ 'TERMS_PARAMS.UPFRONT_CHARGES' | translate }}
            <span class="text-red-500">*</span>
          </label>

          <div class="radio-toggle-group">
            <ng-container *ngFor="let option of yesNoOptions">
              <input
                type="radio"
                [id]="'globalUpfrontCharges_' + option.value"
                formControlName="upfrontCharges"
                [value]="option.value"
              />
              <label [for]="'globalUpfrontCharges_' + option.value">
                {{ option.label | translate }}
              </label>
            </ng-container>
          </div>
        </div>

        <div *ngIf="globalDetailsForm.controls.upfrontCharges.value === 1">
          <label class="form-label mb-2 block">
            {{ 'TERMS_PARAMS.UPFRONT_CHARGES_PERCENTAGE' | translate }}
            <span class="text-red-500">*</span>
          </label>

          <div class="relative">
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              inputmode="decimal"
              formControlName="upfrontChargesPercentage"
              class="form-input w-full pe-10"
              [class.form-error]="
                globalDetailsForm.controls.upfrontChargesPercentage.invalid &&
                (globalDetailsForm.controls.upfrontChargesPercentage.dirty ||
                  globalDetailsForm.controls.upfrontChargesPercentage.touched)
              "
            />
            <span class="percent">%</span>
          </div>

          <p
            *ngIf="
              globalDetailsForm.controls.upfrontChargesPercentage.invalid &&
              (globalDetailsForm.controls.upfrontChargesPercentage.dirty ||
                globalDetailsForm.controls.upfrontChargesPercentage.touched)
            "
            class="form-error-text"
          >
            {{ 'TERMS_PARAMS.UPFRONT_CHARGES_PERCENTAGE_ERROR' | translate }}
          </p>
        </div>
      </div>
    </section>

    <div
      *ngIf="saveSuccess"
      class="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700"
    >
      {{ 'TERMS_PARAMS.SAVED_SUCCESS' | translate }}
    </div>

    <div
      *ngIf="saveError"
      class="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
    >
      {{ saveError | translate }}
    </div>

    <!-- ADD REPAYMENT TERM POPUP -->
    <app-popup
      [show]="showAddTermPopup"
      (showChange)="onAddTermPopupVisibilityChange($event)"
      title="TERMS_PARAMS.ADD_REPAYMENT_TERM"
      type="custom"
      width="w-full max-w-4xl min-w-[300px]"
    >
      <div *ngIf="draftMeta" class="space-y-6">
        <div>
          <label class="form-label mb-2 block">
            {{ 'TERMS_PARAMS.REPAYMENT_TERM' | translate }}
            <span class="text-red-500">*</span>
          </label>

          <select
            [formControl]="draftTermTypeControl"
            class="form-select w-full"
            [class.form-error]="draftTermTypeControl.invalid && draftTermTypeControl.touched"
          >
            <option [ngValue]="null">{{ 'TERMS_PARAMS.SELECT_REPAYMENT_TERM' | translate }}</option>
            <option
              *ngFor="let option of availableTermOptions"
              [ngValue]="option.value"
            >
              {{ option.label | translate }}
            </option>
          </select>

          <p
            *ngIf="draftTermTypeControl.invalid && draftTermTypeControl.touched"
            class="form-error-text"
          >
            {{ 'TERMS_PARAMS.SELECT_TERM_TO_CONTINUE' | translate }}
          </p>

          <p
            *ngIf="!draftTermTypeControl.value"
            class="mt-1 text-xs text-gray-500"
          >
            {{ 'TERMS_PARAMS.DETAILS_APPEAR_HINT' | translate }}
          </p>
        </div>

        <ng-container *ngIf="draftTermTypeControl.value">
          <ng-container
            *ngTemplateOutlet="termFieldsTemplate; context: { meta: draftMeta }"
          ></ng-container>
        </ng-container>

        <p *ngIf="draftError" class="form-error-text">
          {{ draftError | translate }}
        </p>

        <div class="flex flex-col justify-end gap-3 border-t border-gray-100 pt-4 sm:flex-row">
          <button type="button" class="btn-outline" (click)="closeAddTermPopup()">
            {{ 'COMMON.CANCEL' | translate }}
          </button>
          <button type="button" class="btn-primary" (click)="confirmAddTerm()">
            {{ 'TERMS_PARAMS.ADD_TERM' | translate }}
          </button>
        </div>
      </div>
    </app-popup>
  </div>
</div>
